import { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Calendar, Clock, FileText, Upload, Loader2, AlertTriangle, Info, X, File, CheckCircle } from 'lucide-react';
import { leaveApi, balanceApi, calendarApi, documentApi } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';

type Step = 'type' | 'dates' | 'details' | 'review';

interface LeaveTypeOption {
  code: string;
  name: string;
  description: string;
  deductsFromBalance: boolean;
  requiresMotivation: boolean;
}

const leaveTypes: LeaveTypeOption[] = [
  { code: 'ANNUAL', name: 'Annual Leave', description: '24 days per year', deductsFromBalance: true, requiresMotivation: false },
  { code: 'SICK', name: 'Sick Leave', description: '36 days over 3-year cycle', deductsFromBalance: true, requiresMotivation: false },
  { code: 'FAMILY', name: 'Family Responsibility', description: 'Child illness, bereavement', deductsFromBalance: true, requiresMotivation: false },
  { code: 'UNPAID', name: 'Unpaid Leave', description: 'Requires motivation', deductsFromBalance: false, requiresMotivation: true },
  { code: 'STUDY', name: 'Study Leave', description: 'For educational purposes', deductsFromBalance: false, requiresMotivation: true },
  { code: 'SPECIAL', name: 'Special Leave', description: 'Special circumstances', deductsFromBalance: false, requiresMotivation: true },
];

interface BalanceInfo {
  leaveType: string;
  total: number;
  taken: number;
  pending: number;
  available: number;
}

interface Holiday {
  date: string;
  name: string;
}

interface UploadedDocument {
  key: string;
  fileName: string;
  contentType: string;
  documentType: 'MEDICAL_CERTIFICATE' | 'MOTIVATION_LETTER' | 'SUPPORTING_DOCUMENT' | 'OTHER';
  status: 'uploading' | 'success' | 'error';
  progress?: number;
}

const HOURS_PER_DAY = 8;
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
const ALLOWED_TYPES = [
  'application/pdf',
  'image/jpeg',
  'image/png',
  'image/gif',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
];

export default function LeaveApplicationPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [currentStep, setCurrentStep] = useState<Step>('type');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [balances, setBalances] = useState<BalanceInfo[]>([]);
  const [holidays, setHolidays] = useState<Holiday[]>([]);
  const [isLoadingData, setIsLoadingData] = useState(true);
  const [uploadedDocuments, setUploadedDocuments] = useState<UploadedDocument[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  // Track individual weekend day selections with their duration
  interface WeekendDaySelection {
    date: string;
    included: boolean;
    durationType: 'FULL_DAY' | 'HALF_DAY' | 'HOURS';
    hours: number; // 8 = full day, 4 = half day, or custom
  }

  const [formData, setFormData] = useState({
    leaveType: '',
    startDate: '',
    endDate: '',
    durationType: 'FULL_DAY',
    hours: '',
    motivation: '',
  });

  const [weekendSelections, setWeekendSelections] = useState<WeekendDaySelection[]>([]);

  // Fetch balances and holidays on mount
  useEffect(() => {
    const fetchData = async () => {
      if (!user?.userId) {
        console.log('No user.userId available yet, waiting...');
        return;
      }

      console.log('Fetching balance for userId:', user.userId, 'email:', user.email);
      setIsLoadingData(true);
      setError('');
      try {
        const [balanceData, holidayData] = await Promise.all([
          balanceApi.getBalance(user.userId) as Promise<{ balances: BalanceInfo[] }>,
          calendarApi.getHolidays(new Date().getFullYear().toString()) as Promise<{ holidays: Holiday[] }>,
        ]);
        console.log('Balance data received:', balanceData);
        setBalances(balanceData.balances || []);
        setHolidays(holidayData.holidays || []);
      } catch (err: any) {
        console.error('Error fetching data:', err);
        setError(err.message || 'Failed to load leave data. Please refresh the page.');
      } finally {
        setIsLoadingData(false);
      }
    };

    fetchData();
  }, [user?.userId]);

  // Get balance for selected leave type
  const selectedTypeBalance = useMemo(() => {
    if (!formData.leaveType) return null;
    return balances.find(b => b.leaveType === formData.leaveType) || null;
  }, [formData.leaveType, balances]);

  // Get selected leave type config
  const selectedTypeConfig = useMemo(() => {
    return leaveTypes.find(t => t.code === formData.leaveType) || null;
  }, [formData.leaveType]);

  // Format date to YYYY-MM-DD without timezone conversion
  const formatDateLocal = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Get holiday name for a date
  const getHolidayName = (dateStr: string): string | null => {
    const holiday = holidays.find(h => h.date === dateStr);
    return holiday?.name || null;
  };

  // Detailed day breakdown
  interface DayBreakdown {
    date: string;
    dayName: string;
    type: 'weekday' | 'weekend' | 'holiday';
    holidayName?: string;
    included: boolean;
    dayValue: number; // 1 for full day, 0.5 for half, or hours/8
  }

  // Calculate detailed working days between two dates
  const dayBreakdown = useMemo((): {
    days: DayBreakdown[];
    workingDays: number;
    weekendDays: number;
    holidayDays: number;
    totalDays: number;
    effectiveDays: number;
    weekendDates: string[];
  } => {
    if (!formData.startDate || !formData.endDate) {
      return { days: [], workingDays: 0, weekendDays: 0, holidayDays: 0, totalDays: 0, effectiveDays: 0, weekendDates: [] };
    }

    const start = new Date(formData.startDate + 'T00:00:00');
    const end = new Date(formData.endDate + 'T00:00:00');

    if (start > end) {
      return { days: [], workingDays: 0, weekendDays: 0, holidayDays: 0, totalDays: 0, effectiveDays: 0, weekendDates: [] };
    }

    const days: DayBreakdown[] = [];
    const weekendDates: string[] = [];
    let workingDays = 0;
    let weekendDays = 0;
    let holidayDays = 0;
    const current = new Date(start);
    const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

    while (current <= end) {
      const dateStr = formatDateLocal(current);
      const dayOfWeek = current.getDay();
      const isWeekendDay = dayOfWeek === 0 || dayOfWeek === 6;
      const holidayName = getHolidayName(dateStr);

      let type: 'weekday' | 'weekend' | 'holiday';
      let included: boolean;
      let dayValue: number = 1;

      if (holidayName) {
        type = 'holiday';
        holidayDays++;
        included = false;
        dayValue = 0;
      } else if (isWeekendDay) {
        type = 'weekend';
        weekendDays++;
        weekendDates.push(dateStr);
        // Check if this weekend day is selected
        const selection = weekendSelections.find(s => s.date === dateStr);
        included = selection?.included || false;
        if (included && selection) {
          dayValue = selection.hours / 8; // Convert hours to day value
        } else {
          dayValue = 0;
        }
      } else {
        type = 'weekday';
        workingDays++;
        included = true;
        dayValue = 1;
      }

      days.push({
        date: dateStr,
        dayName: dayNames[dayOfWeek],
        type,
        holidayName: holidayName || undefined,
        included,
        dayValue,
      });

      current.setDate(current.getDate() + 1);
    }

    const totalDays = days.length;
    const effectiveDays = days.reduce((sum, d) => sum + d.dayValue, 0);

    return { days, workingDays, weekendDays, holidayDays, totalDays, effectiveDays, weekendDates };
  }, [formData.startDate, formData.endDate, weekendSelections, holidays]);

  // Initialize weekend selections when dates change
  useEffect(() => {
    if (dayBreakdown.weekendDates.length > 0) {
      setWeekendSelections(prev => {
        // Keep existing selections for dates that still exist, add new ones
        const newSelections: WeekendDaySelection[] = dayBreakdown.weekendDates.map(date => {
          const existing = prev.find(s => s.date === date);
          return existing || { date, included: false, durationType: 'FULL_DAY' as const, hours: 8 };
        });
        return newSelections;
      });
    } else {
      setWeekendSelections([]);
    }
  }, [dayBreakdown.weekendDates.join(',')]);

  // Legacy calculation for compatibility
  const calculateWorkingDays = useMemo(() => {
    return {
      workingDays: dayBreakdown.effectiveDays,
      totalDays: dayBreakdown.totalDays,
      excludedDays: dayBreakdown.totalDays - dayBreakdown.effectiveDays,
    };
  }, [dayBreakdown]);

  // Calculate requested leave days based on duration type
  const requestedDays = useMemo(() => {
    const { workingDays } = calculateWorkingDays;

    if (formData.durationType === 'HALF_DAY') {
      return workingDays * 0.5;
    } else if (formData.durationType === 'HOURS' && formData.hours) {
      return parseFloat(formData.hours) / HOURS_PER_DAY;
    }

    return workingDays;
  }, [calculateWorkingDays, formData.durationType, formData.hours]);

  // Calculate projected balance after this leave
  const projectedBalance = useMemo(() => {
    if (!selectedTypeBalance || !selectedTypeConfig?.deductsFromBalance) return null;
    return selectedTypeBalance.available - requestedDays;
  }, [selectedTypeBalance, selectedTypeConfig, requestedDays]);

  // Check if balance is insufficient
  const hasInsufficientBalance = useMemo(() => {
    if (!selectedTypeConfig?.deductsFromBalance) return false;
    if (!selectedTypeBalance) return false;
    return requestedDays > selectedTypeBalance.available;
  }, [selectedTypeConfig, selectedTypeBalance, requestedDays]);

  const steps: { key: Step; label: string; icon: React.ElementType }[] = [
    { key: 'type', label: 'Leave Type', icon: FileText },
    { key: 'dates', label: 'Dates', icon: Calendar },
    { key: 'details', label: 'Details', icon: Clock },
    { key: 'review', label: 'Review', icon: Upload },
  ];

  const currentStepIndex = steps.findIndex(s => s.key === currentStep);

  const handleNext = () => {
    const nextIndex = currentStepIndex + 1;
    if (nextIndex < steps.length) {
      setCurrentStep(steps[nextIndex].key);
    }
  };

  const handleBack = () => {
    const prevIndex = currentStepIndex - 1;
    if (prevIndex >= 0) {
      setCurrentStep(steps[prevIndex].key);
    } else {
      navigate('/leave');
    }
  };

  const handleSwitchToUnpaidLeave = () => {
    setFormData({ ...formData, leaveType: 'UNPAID' });
  };

  const handleSwitchToSpecialLeave = () => {
    setFormData({ ...formData, leaveType: 'SPECIAL' });
  };

  // Determine document type based on leave type
  const getDefaultDocumentType = (): UploadedDocument['documentType'] => {
    if (formData.leaveType === 'SICK') return 'MEDICAL_CERTIFICATE';
    if (['UNPAID', 'STUDY', 'SPECIAL'].includes(formData.leaveType)) return 'MOTIVATION_LETTER';
    return 'SUPPORTING_DOCUMENT';
  };

  // Handle file selection
  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    for (const file of Array.from(files)) {
      await uploadFile(file);
    }

    // Clear the input so the same file can be selected again
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Handle drag and drop
  const handleDrop = async (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();

    const files = event.dataTransfer.files;
    if (!files || files.length === 0) return;

    for (const file of Array.from(files)) {
      await uploadFile(file);
    }
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
  };

  // Upload a single file
  const uploadFile = async (file: File) => {
    // Validate file size
    if (file.size > MAX_FILE_SIZE) {
      setError(`File "${file.name}" is too large. Maximum size is 10MB.`);
      return;
    }

    // Validate file type
    if (!ALLOWED_TYPES.includes(file.type)) {
      setError(`File "${file.name}" is not a supported type. Allowed: PDF, JPG, PNG, GIF, DOC, DOCX.`);
      return;
    }

    // Add to uploading state
    const tempKey = `temp-${Date.now()}-${file.name}`;
    const newDoc: UploadedDocument = {
      key: tempKey,
      fileName: file.name,
      contentType: file.type,
      documentType: getDefaultDocumentType(),
      status: 'uploading',
      progress: 0,
    };

    setUploadedDocuments(prev => [...prev, newDoc]);
    setIsUploading(true);
    setError('');

    try {
      // Get presigned upload URL
      const { uploadUrl, key } = await documentApi.getUploadUrl({
        fileName: file.name,
        contentType: file.type,
        documentType: getDefaultDocumentType(),
      }) as { uploadUrl: string; key: string };

      // Upload to S3
      const response = await fetch(uploadUrl, {
        method: 'PUT',
        headers: {
          'Content-Type': file.type,
        },
        body: file,
      });

      if (!response.ok) {
        throw new Error('Upload failed');
      }

      // Update document status
      setUploadedDocuments(prev =>
        prev.map(doc =>
          doc.key === tempKey
            ? { ...doc, key, status: 'success' as const }
            : doc
        )
      );
    } catch (err) {
      console.error('Error uploading file:', err);
      setUploadedDocuments(prev =>
        prev.map(doc =>
          doc.key === tempKey
            ? { ...doc, status: 'error' as const }
            : doc
        )
      );
      setError(`Failed to upload "${file.name}". Please try again.`);
    } finally {
      setIsUploading(false);
    }
  };

  // Remove a document
  const removeDocument = (key: string) => {
    setUploadedDocuments(prev => prev.filter(doc => doc.key !== key));
  };

  const handleSubmit = async () => {
    // Final validation
    if (hasInsufficientBalance) {
      setError('Insufficient leave balance. Please apply for Unpaid Leave or Special Leave instead.');
      return;
    }

    // Check if there are any failed uploads
    const failedUploads = uploadedDocuments.filter(doc => doc.status === 'error');
    if (failedUploads.length > 0) {
      setError('Please remove or re-upload failed documents before submitting.');
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      // Prepare documents for submission (only successful uploads)
      const documents = uploadedDocuments
        .filter(doc => doc.status === 'success')
        .map(doc => ({
          key: doc.key,
          fileName: doc.fileName,
          contentType: doc.contentType,
          documentType: doc.documentType,
        }));

      // Prepare day breakdown for backend
      const excludedDates = dayBreakdown.days
        .filter(d => !d.included)
        .map(d => ({
          date: d.date,
          reason: d.type === 'holiday' ? `Public Holiday: ${d.holidayName}` : 'Weekend',
        }));

      // Prepare weekend day details
      const includedWeekendDays = weekendSelections
        .filter(s => s.included)
        .map(s => ({
          date: s.date,
          hours: s.hours,
          dayValue: s.hours / 8,
        }));

      const payload = {
        leaveType: formData.leaveType,
        startDate: formData.startDate,
        endDate: formData.endDate,
        durationType: formData.durationType,
        hours: formData.durationType === 'HOURS' ? parseFloat(formData.hours) : undefined,
        motivation: formData.motivation || undefined,
        documents: documents.length > 0 ? documents : undefined,
        // Day calculation details for approver
        calculatedDays: dayBreakdown.effectiveDays,
        totalCalendarDays: dayBreakdown.totalDays,
        includesWeekends: includedWeekendDays.length > 0,
        weekendDays: includedWeekendDays.length > 0 ? includedWeekendDays : undefined,
        excludedDates: excludedDates.length > 0 ? excludedDates : undefined,
      };

      await leaveApi.submitApplication(payload);
      navigate('/leave', { state: { message: 'Leave application submitted successfully!' } });
    } catch (err: any) {
      console.error('Error submitting application:', err);
      setError(err.message || 'Failed to submit application. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Balance info card component
  const BalanceInfoCard = () => {
    if (!selectedTypeConfig) return null;

    // For leave types that don't deduct from balance
    if (!selectedTypeConfig.deductsFromBalance) {
      return (
        <div className="rounded-lg bg-blue-50 border border-blue-200 p-4">
          <div className="flex items-start gap-3">
            <Info className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-medium text-blue-900">{selectedTypeConfig.name}</p>
              <p className="text-sm text-blue-700 mt-1">
                This leave type does not deduct from your balance and requires approval.
              </p>
            </div>
          </div>
        </div>
      );
    }

    // For leave types that deduct from balance
    return (
      <div className={`rounded-lg border p-4 ${hasInsufficientBalance ? 'bg-red-50 border-red-200' : 'bg-green-50 border-green-200'}`}>
        <h3 className="font-medium text-gray-900 mb-3">Leave Balance</h3>
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div>
            <p className="text-gray-500">Current Balance</p>
            <p className="text-lg font-semibold text-gray-900">
              {selectedTypeBalance ? `${selectedTypeBalance.available} days` : 'Loading...'}
            </p>
          </div>
          <div>
            <p className="text-gray-500">Requesting</p>
            <p className={`text-lg font-semibold ${requestedDays > 0 ? 'text-amber-600' : 'text-gray-400'}`}>
              {requestedDays > 0 ? `${requestedDays.toFixed(requestedDays % 1 !== 0 ? 1 : 0)} days` : '-'}
            </p>
          </div>
          {requestedDays > 0 && projectedBalance !== null && (
            <>
              <div className="col-span-2 border-t border-gray-200 pt-3">
                <div className="flex justify-between items-center">
                  <span className="text-gray-500">Balance After Approval</span>
                  <span className={`text-lg font-bold ${projectedBalance < 0 ? 'text-red-600' : 'text-green-600'}`}>
                    {projectedBalance.toFixed(projectedBalance % 1 !== 0 ? 1 : 0)} days
                  </span>
                </div>
              </div>
            </>
          )}
        </div>

        {hasInsufficientBalance && (
          <div className="mt-4 pt-4 border-t border-red-200">
            <div className="flex items-start gap-2">
              <AlertTriangle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium text-red-800">Insufficient Balance</p>
                <p className="text-sm text-red-700 mt-1">
                  You don't have enough {selectedTypeConfig.name} balance for this request.
                  Consider applying for:
                </p>
                <div className="flex gap-2 mt-3">
                  <button
                    onClick={handleSwitchToUnpaidLeave}
                    className="btn-secondary text-sm py-1.5 px-3"
                  >
                    Unpaid Leave
                  </button>
                  <button
                    onClick={handleSwitchToSpecialLeave}
                    className="btn-secondary text-sm py-1.5 px-3"
                  >
                    Special Leave
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  if (isLoadingData) {
    return (
      <div className="flex flex-col items-center justify-center py-12 gap-4">
        <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
        <p className="text-sm text-gray-500">Loading leave data...</p>
      </div>
    );
  }

  // Show error if balance fetch failed and no balances loaded
  if (error && balances.length === 0) {
    return (
      <div className="mx-auto max-w-3xl space-y-6">
        <div>
          <button
            onClick={() => navigate('/leave')}
            className="mb-4 flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900"
          >
            <ArrowLeft className="h-4 w-4" />
            Back
          </button>
          <h1 className="text-2xl font-bold text-gray-900">Apply for Leave</h1>
        </div>
        <div className="card p-6">
          <div className="rounded-lg bg-red-50 border border-red-200 p-4">
            <div className="flex items-start gap-3">
              <AlertTriangle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium text-red-900">Unable to Load Leave Data</p>
                <p className="text-sm text-red-700 mt-1">{error}</p>
                <button
                  onClick={() => window.location.reload()}
                  className="btn-secondary mt-4"
                >
                  Refresh Page
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      {/* Header */}
      <div>
        <button
          onClick={handleBack}
          className="mb-4 flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900"
        >
          <ArrowLeft className="h-4 w-4" />
          Back
        </button>
        <h1 className="text-2xl font-bold text-gray-900">Apply for Leave</h1>
      </div>

      {/* Progress Steps */}
      <div className="card p-4">
        <div className="flex items-center justify-between">
          {steps.map((step, index) => (
            <div key={step.key} className="flex items-center">
              <div
                className={`flex h-10 w-10 items-center justify-center rounded-full ${
                  index <= currentStepIndex
                    ? 'bg-primary-600 text-white'
                    : 'bg-gray-200 text-gray-500'
                }`}
              >
                <step.icon className="h-5 w-5" />
              </div>
              <span
                className={`ml-2 text-sm font-medium ${
                  index <= currentStepIndex ? 'text-gray-900' : 'text-gray-500'
                }`}
              >
                {step.label}
              </span>
              {index < steps.length - 1 && (
                <div
                  className={`mx-4 h-0.5 w-16 ${
                    index < currentStepIndex ? 'bg-primary-600' : 'bg-gray-200'
                  }`}
                />
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Step Content */}
      <div className="card p-6">
        {currentStep === 'type' && (
          <div className="space-y-4">
            <h2 className="text-lg font-semibold">Select Leave Type</h2>
            <div className="grid gap-4 md:grid-cols-2">
              {leaveTypes.map(type => {
                const balance = balances.find(b => b.leaveType === type.code);
                return (
                  <button
                    key={type.code}
                    onClick={() => {
                      setFormData({ ...formData, leaveType: type.code });
                      handleNext();
                    }}
                    className={`rounded-lg border p-4 text-left transition-colors hover:border-primary-500 hover:bg-primary-50 ${
                      formData.leaveType === type.code
                        ? 'border-primary-500 bg-primary-50'
                        : 'border-gray-200'
                    }`}
                  >
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="font-medium text-gray-900">{type.name}</p>
                        <p className="text-sm text-gray-500">{type.description}</p>
                      </div>
                      {type.deductsFromBalance && balance && (
                        <span className={`text-sm font-medium px-2 py-0.5 rounded ${
                          balance.available > 0 ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                        }`}>
                          {balance.available} days
                        </span>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {currentStep === 'dates' && (
          <div className="space-y-6">
            <h2 className="text-lg font-semibold">Select Dates</h2>

            {/* Balance Info Card */}
            <BalanceInfoCard />

            {/* Duration Type - moved above dates */}
            <div>
              <label className="label">Duration Type</label>
              <select
                value={formData.durationType}
                onChange={e => {
                  const newType = e.target.value;
                  // If switching to half day or hours, sync end date with start date
                  if (newType === 'HALF_DAY' || newType === 'HOURS') {
                    setFormData({ ...formData, durationType: newType, endDate: formData.startDate });
                  } else {
                    setFormData({ ...formData, durationType: newType });
                  }
                }}
                className="input mt-1"
              >
                <option value="FULL_DAY">Full Day(s)</option>
                <option value="HALF_DAY">Half Day</option>
                <option value="HOURS">Hours</option>
              </select>
            </div>

            {/* Info message for half day / hours */}
            {(formData.durationType === 'HALF_DAY' || formData.durationType === 'HOURS') && (
              <div className="rounded-lg bg-blue-50 border border-blue-200 p-4">
                <div className="flex items-start gap-3">
                  <Info className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium text-blue-900">Single Day Only</p>
                    <p className="text-sm text-blue-700 mt-1">
                      {formData.durationType === 'HALF_DAY'
                        ? 'Half day leave can only be taken on a single day. If you need half days on multiple dates, please submit separate applications for each day.'
                        : 'Hourly leave can only be taken on a single day. If you need hours on multiple dates, please submit separate applications for each day.'}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Date selection */}
            {formData.durationType === 'FULL_DAY' ? (
              <div className="grid gap-6 md:grid-cols-2">
                <div>
                  <label className="label">Start Date</label>
                  <input
                    type="date"
                    value={formData.startDate}
                    onChange={e => setFormData({ ...formData, startDate: e.target.value })}
                    className="input mt-1"
                    min={formData.leaveType === 'SICK' ? undefined : new Date().toISOString().split('T')[0]}
                  />
                </div>
                <div>
                  <label className="label">End Date</label>
                  <input
                    type="date"
                    value={formData.endDate}
                    onChange={e => setFormData({ ...formData, endDate: e.target.value })}
                    className="input mt-1"
                    min={formData.startDate || undefined}
                  />
                </div>
              </div>
            ) : (
              <div>
                <label className="label">Date</label>
                <input
                  type="date"
                  value={formData.startDate}
                  onChange={e => setFormData({ ...formData, startDate: e.target.value, endDate: e.target.value })}
                  className="input mt-1"
                  min={formData.leaveType === 'SICK' ? undefined : new Date().toISOString().split('T')[0]}
                />
              </div>
            )}

            {/* Show detailed day breakdown - only for full days */}
            {formData.durationType === 'FULL_DAY' && formData.startDate && formData.endDate && dayBreakdown.totalDays > 0 && (
              <div className="space-y-4">
                {/* Day breakdown summary */}
                <div className="rounded-lg bg-gray-50 p-4">
                  <h4 className="font-medium text-gray-900 mb-3">Day Breakdown</h4>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Total calendar days:</span>
                      <span className="font-medium">{dayBreakdown.totalDays}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Weekdays:</span>
                      <span className="font-medium text-green-600">{dayBreakdown.workingDays}</span>
                    </div>
                    {dayBreakdown.weekendDays > 0 && (
                      <div className="flex justify-between">
                        <span className="text-gray-600">Weekend days:</span>
                        <span className={`font-medium ${weekendSelections.some(s => s.included) ? 'text-amber-600' : 'text-gray-400'}`}>
                          {weekendSelections.some(s => s.included)
                            ? `${weekendSelections.filter(s => s.included).reduce((sum, s) => sum + s.hours / 8, 0).toFixed(1)} included`
                            : `${dayBreakdown.weekendDays} (excluded)`
                          }
                        </span>
                      </div>
                    )}
                    {dayBreakdown.holidayDays > 0 && (
                      <div className="flex justify-between">
                        <span className="text-gray-600">Public holidays:</span>
                        <span className="font-medium text-gray-400">{dayBreakdown.holidayDays} (excluded)</span>
                      </div>
                    )}
                  </div>
                  <div className="mt-3 pt-3 border-t border-gray-200">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-900 font-medium">Effective leave days:</span>
                      <span className="text-lg font-bold text-primary-600">{dayBreakdown.effectiveDays}</span>
                    </div>
                  </div>
                </div>

                {/* Detailed day list */}
                {dayBreakdown.days.length <= 14 && (
                  <div className="rounded-lg border border-gray-200 overflow-hidden">
                    <div className="bg-gray-50 px-4 py-2 border-b border-gray-200">
                      <h4 className="text-sm font-medium text-gray-700">Day-by-day breakdown</h4>
                    </div>
                    <div className="divide-y divide-gray-100 max-h-64 overflow-y-auto">
                      {dayBreakdown.days.map((day) => {
                        const weekendSelection = day.type === 'weekend' ? weekendSelections.find(s => s.date === day.date) : null;
                        return (
                          <div
                            key={day.date}
                            className={`px-4 py-2 flex items-center justify-between text-sm ${
                              !day.included ? 'bg-gray-50 text-gray-400' : ''
                            }`}
                          >
                            <div className="flex items-center gap-3">
                              <span className={`w-2 h-2 rounded-full ${
                                day.type === 'holiday' ? 'bg-pink-400' :
                                day.type === 'weekend' ? (day.included ? 'bg-amber-400' : 'bg-gray-300') :
                                'bg-green-400'
                              }`} />
                              <span className={day.included ? 'text-gray-900' : 'text-gray-400'}>
                                {day.dayName}, {new Date(day.date + 'T00:00:00').toLocaleDateString('en-ZA', { day: 'numeric', month: 'short' })}
                              </span>
                            </div>
                            <div className="flex items-center gap-2">
                              {day.type === 'holiday' && (
                                <span className="text-xs px-2 py-0.5 rounded bg-pink-100 text-pink-700">
                                  {day.holidayName}
                                </span>
                              )}
                              {day.type === 'weekend' && (
                                <span className={`text-xs px-2 py-0.5 rounded ${
                                  day.included ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-500'
                                }`}>
                                  {day.included && weekendSelection
                                    ? (weekendSelection.hours === 8 ? 'Full day' : weekendSelection.hours === 4 ? 'Half day' : `${weekendSelection.hours}h`)
                                    : 'Weekend'
                                  }
                                </span>
                              )}
                              {day.included ? (
                                <span className="text-xs font-medium text-green-600">{day.dayValue === 1 ? '1' : day.dayValue.toFixed(1)}</span>
                              ) : (
                                <X className="h-4 w-4 text-gray-300" />
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Weekend days selection */}
                {dayBreakdown.weekendDays > 0 && (
                  <div className="rounded-lg border border-amber-200 bg-amber-50 p-4">
                    <div className="flex items-start gap-3">
                      <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                      <div className="flex-1">
                        <p className="font-medium text-amber-900">Weekend days in your leave period</p>
                        <p className="text-sm text-amber-700 mt-1">
                          Your dates include {dayBreakdown.weekendDays} weekend day{dayBreakdown.weekendDays > 1 ? 's' : ''}.
                          If you work weekends, select the days to include and specify the duration for each.
                        </p>

                        {/* Individual weekend day selection */}
                        <div className="mt-4 space-y-3">
                          {weekendSelections.map((selection) => {
                            const dayInfo = dayBreakdown.days.find(d => d.date === selection.date);
                            if (!dayInfo) return null;

                            return (
                              <div
                                key={selection.date}
                                className={`rounded-lg border p-3 ${selection.included ? 'bg-white border-amber-300' : 'bg-amber-50/50 border-amber-100'}`}
                              >
                                <div className="flex items-start gap-3">
                                  <input
                                    type="checkbox"
                                    checked={selection.included}
                                    onChange={(e) => {
                                      setWeekendSelections(prev => prev.map(s =>
                                        s.date === selection.date
                                          ? { ...s, included: e.target.checked }
                                          : s
                                      ));
                                    }}
                                    className="h-4 w-4 mt-1 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                                  />
                                  <div className="flex-1">
                                    <div className="flex items-center justify-between">
                                      <span className={`font-medium ${selection.included ? 'text-gray-900' : 'text-gray-500'}`}>
                                        {dayInfo.dayName}, {new Date(selection.date + 'T00:00:00').toLocaleDateString('en-ZA', { day: 'numeric', month: 'short' })}
                                      </span>
                                      {selection.included && (
                                        <span className="text-sm font-medium text-primary-600">
                                          {selection.hours === 8 ? '1 day' : selection.hours === 4 ? '0.5 days' : `${(selection.hours / 8).toFixed(2)} days`}
                                        </span>
                                      )}
                                    </div>

                                    {selection.included && (
                                      <div className="mt-2 flex items-center gap-2 flex-wrap">
                                        <span className="text-xs text-gray-500">Duration:</span>
                                        <div className="flex gap-1">
                                          <button
                                            type="button"
                                            onClick={() => setWeekendSelections(prev => prev.map(s =>
                                              s.date === selection.date
                                                ? { ...s, durationType: 'FULL_DAY', hours: 8 }
                                                : s
                                            ))}
                                            className={`px-2 py-1 text-xs rounded ${
                                              selection.durationType === 'FULL_DAY'
                                                ? 'bg-primary-600 text-white'
                                                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                            }`}
                                          >
                                            Full day
                                          </button>
                                          <button
                                            type="button"
                                            onClick={() => setWeekendSelections(prev => prev.map(s =>
                                              s.date === selection.date
                                                ? { ...s, durationType: 'HALF_DAY', hours: 4 }
                                                : s
                                            ))}
                                            className={`px-2 py-1 text-xs rounded ${
                                              selection.durationType === 'HALF_DAY'
                                                ? 'bg-primary-600 text-white'
                                                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                            }`}
                                          >
                                            Half day
                                          </button>
                                          <button
                                            type="button"
                                            onClick={() => setWeekendSelections(prev => prev.map(s =>
                                              s.date === selection.date
                                                ? { ...s, durationType: 'HOURS', hours: s.hours === 8 || s.hours === 4 ? 2 : s.hours }
                                                : s
                                            ))}
                                            className={`px-2 py-1 text-xs rounded ${
                                              selection.durationType === 'HOURS'
                                                ? 'bg-primary-600 text-white'
                                                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                            }`}
                                          >
                                            Hours
                                          </button>
                                        </div>
                                        {selection.durationType === 'HOURS' && (
                                          <input
                                            type="number"
                                            min="0.5"
                                            max="8"
                                            step="0.5"
                                            value={selection.hours}
                                            onChange={(e) => setWeekendSelections(prev => prev.map(s =>
                                              s.date === selection.date
                                                ? { ...s, hours: parseFloat(e.target.value) || 0 }
                                                : s
                                            ))}
                                            className="w-16 px-2 py-1 text-xs border rounded"
                                            placeholder="hrs"
                                          />
                                        )}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>

                        {/* Summary of included weekend days */}
                        {weekendSelections.some(s => s.included) && (
                          <div className="mt-3 pt-3 border-t border-amber-200">
                            <div className="flex justify-between text-sm">
                              <span className="text-amber-800">Weekend leave total:</span>
                              <span className="font-medium text-amber-900">
                                {weekendSelections
                                  .filter(s => s.included)
                                  .reduce((sum, s) => sum + s.hours / 8, 0)
                                  .toFixed(1)} days
                              </span>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* Holiday info */}
                {dayBreakdown.holidayDays > 0 && (
                  <div className="rounded-lg bg-pink-50 border border-pink-200 p-4">
                    <div className="flex items-start gap-3">
                      <Info className="h-5 w-5 text-pink-600 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="font-medium text-pink-900">Public holidays excluded</p>
                        <p className="text-sm text-pink-700 mt-1">
                          {dayBreakdown.holidayDays} public holiday{dayBreakdown.holidayDays > 1 ? 's are' : ' is'} within your selected dates and will not be counted as leave:
                        </p>
                        <ul className="mt-2 space-y-1">
                          {dayBreakdown.days
                            .filter(d => d.type === 'holiday')
                            .map(d => (
                              <li key={d.date} className="text-sm text-pink-800">
                                • {d.holidayName} ({new Date(d.date + 'T00:00:00').toLocaleDateString('en-ZA', { weekday: 'short', day: 'numeric', month: 'short' })})
                              </li>
                            ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {formData.durationType === 'HOURS' && (
              <div>
                <label className="label">Number of Hours</label>
                <input
                  type="number"
                  min="0.5"
                  max="8"
                  step="0.5"
                  value={formData.hours}
                  onChange={e => setFormData({ ...formData, hours: e.target.value })}
                  className="input mt-1"
                  placeholder="e.g., 4"
                />
                <p className="mt-1 text-xs text-gray-500">8 accumulated hours = 1 full day</p>
              </div>
            )}
            <div className="flex justify-end gap-4">
              <button onClick={handleBack} className="btn-secondary">
                Back
              </button>
              <button
                onClick={handleNext}
                disabled={!formData.startDate || (formData.durationType === 'FULL_DAY' && !formData.endDate) || hasInsufficientBalance || (formData.durationType === 'HOURS' && !formData.hours)}
                className="btn-primary"
              >
                Next
              </button>
            </div>
          </div>
        )}

        {currentStep === 'details' && (
          <div className="space-y-6">
            <h2 className="text-lg font-semibold">Additional Details</h2>

            {/* Balance Info Card */}
            <BalanceInfoCard />

            <div>
              <label className="label">
                Motivation / Reason
                {selectedTypeConfig?.requiresMotivation && <span className="text-red-500 ml-1">*</span>}
              </label>
              <textarea
                value={formData.motivation}
                onChange={e => setFormData({ ...formData, motivation: e.target.value })}
                className="input mt-1 h-32"
                placeholder="Provide a reason for your leave request..."
                required={selectedTypeConfig?.requiresMotivation}
              />
              {selectedTypeConfig?.requiresMotivation && (
                <p className="mt-1 text-xs text-amber-600">Motivation is required for {selectedTypeConfig.name}</p>
              )}
            </div>
            <div>
              <label className="label">Supporting Documents</label>

              {/* Uploaded files list */}
              {uploadedDocuments.length > 0 && (
                <div className="mt-2 space-y-2 mb-4">
                  {uploadedDocuments.map((doc) => (
                    <div
                      key={doc.key}
                      className={`flex items-center justify-between rounded-lg border p-3 ${
                        doc.status === 'error'
                          ? 'border-red-300 bg-red-50'
                          : doc.status === 'uploading'
                          ? 'border-blue-300 bg-blue-50'
                          : 'border-green-300 bg-green-50'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <File className={`h-5 w-5 ${
                          doc.status === 'error'
                            ? 'text-red-500'
                            : doc.status === 'uploading'
                            ? 'text-blue-500'
                            : 'text-green-500'
                        }`} />
                        <div>
                          <p className="text-sm font-medium text-gray-900">{doc.fileName}</p>
                          <p className="text-xs text-gray-500">
                            {doc.status === 'uploading' && 'Uploading...'}
                            {doc.status === 'success' && 'Uploaded successfully'}
                            {doc.status === 'error' && 'Upload failed'}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {doc.status === 'success' && (
                          <CheckCircle className="h-5 w-5 text-green-500" />
                        )}
                        {doc.status === 'uploading' && (
                          <Loader2 className="h-5 w-5 text-blue-500 animate-spin" />
                        )}
                        <button
                          type="button"
                          onClick={() => removeDocument(doc.key)}
                          className="rounded-full p-1 hover:bg-gray-200"
                          disabled={doc.status === 'uploading'}
                        >
                          <X className="h-4 w-4 text-gray-500" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Upload area */}
              <div
                onClick={() => fileInputRef.current?.click()}
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                className={`mt-1 flex cursor-pointer items-center justify-center rounded-lg border-2 border-dashed px-6 py-10 transition-colors ${
                  isUploading
                    ? 'border-blue-400 bg-blue-50'
                    : 'border-gray-300 hover:border-primary-400 hover:bg-primary-50'
                }`}
              >
                <div className="text-center">
                  {isUploading ? (
                    <Loader2 className="mx-auto h-12 w-12 text-blue-500 animate-spin" />
                  ) : (
                    <Upload className="mx-auto h-12 w-12 text-gray-400" />
                  )}
                  <p className="mt-2 text-sm text-gray-600">
                    {isUploading ? 'Uploading...' : 'Drop files here or click to upload'}
                  </p>
                  <p className="text-xs text-gray-500">
                    PDF, JPG, PNG, DOC, DOCX (max 10MB each)
                  </p>
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  accept=".pdf,.jpg,.jpeg,.png,.gif,.doc,.docx"
                  onChange={handleFileSelect}
                  className="hidden"
                />
              </div>

              {formData.leaveType === 'SICK' && requestedDays > 2 && uploadedDocuments.filter(d => d.status === 'success').length === 0 && (
                <div className="mt-2 rounded-lg bg-amber-50 border border-amber-200 p-3">
                  <div className="flex items-center gap-2">
                    <AlertTriangle className="h-4 w-4 text-amber-600" />
                    <p className="text-sm text-amber-700">
                      A medical certificate is required for sick leave over 2 days.
                    </p>
                  </div>
                </div>
              )}
            </div>
            <div className="flex justify-end gap-4">
              <button onClick={handleBack} className="btn-secondary">
                Back
              </button>
              <button
                onClick={handleNext}
                disabled={selectedTypeConfig?.requiresMotivation && !formData.motivation.trim()}
                className="btn-primary"
              >
                Review
              </button>
            </div>
          </div>
        )}

        {currentStep === 'review' && (
          <div className="space-y-6">
            <h2 className="text-lg font-semibold">Review Application</h2>

            {error && (
              <div className="rounded-md bg-red-50 p-4">
                <p className="text-sm text-red-700">{error}</p>
              </div>
            )}

            {/* Balance Info Card */}
            <BalanceInfoCard />

            <div className="rounded-lg bg-gray-50 p-4">
              <dl className="space-y-4">
                <div className="flex justify-between">
                  <dt className="text-gray-500">Leave Type</dt>
                  <dd className="font-medium text-gray-900">
                    {leaveTypes.find(t => t.code === formData.leaveType)?.name}
                  </dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-gray-500">Start Date</dt>
                  <dd className="font-medium text-gray-900">
                    {formData.startDate ? new Date(formData.startDate).toLocaleDateString('en-ZA', {
                      weekday: 'long',
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    }) : '-'}
                  </dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-gray-500">End Date</dt>
                  <dd className="font-medium text-gray-900">
                    {formData.endDate ? new Date(formData.endDate).toLocaleDateString('en-ZA', {
                      weekday: 'long',
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    }) : '-'}
                  </dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-gray-500">Duration</dt>
                  <dd className="font-medium text-gray-900">
                    {formData.durationType === 'HOURS'
                      ? `${formData.hours} hours`
                      : formData.durationType.replace('_', ' ')}
                  </dd>
                </div>
                {/* Day calculation breakdown in review */}
                {formData.durationType === 'FULL_DAY' && dayBreakdown.totalDays > 0 && (
                  <div className="border-t border-gray-200 pt-4 space-y-2">
                    <div className="flex justify-between text-sm">
                      <dt className="text-gray-500">Calendar days</dt>
                      <dd className="text-gray-700">{dayBreakdown.totalDays}</dd>
                    </div>
                    {dayBreakdown.holidayDays > 0 && (
                      <div className="flex justify-between text-sm">
                        <dt className="text-gray-500">Public holidays (excluded)</dt>
                        <dd className="text-pink-600">-{dayBreakdown.holidayDays}</dd>
                      </div>
                    )}
                    {dayBreakdown.weekendDays > 0 && (
                      <div className="flex justify-between text-sm">
                        <dt className="text-gray-500">
                          Weekend days {weekendSelections.some(s => s.included) ? '(partial included)' : '(excluded)'}
                        </dt>
                        <dd className={weekendSelections.some(s => s.included) ? 'text-amber-600' : 'text-gray-400'}>
                          {weekendSelections.some(s => s.included)
                            ? `+${weekendSelections.filter(s => s.included).reduce((sum, s) => sum + s.hours / 8, 0).toFixed(1)}`
                            : `-${dayBreakdown.weekendDays}`
                          }
                        </dd>
                      </div>
                    )}
                  </div>
                )}
                <div className="flex justify-between border-t border-gray-200 pt-4">
                  <dt className="text-gray-900 font-medium">Total Leave Days</dt>
                  <dd className="text-lg font-bold text-primary-600">
                    {requestedDays.toFixed(requestedDays % 1 !== 0 ? 1 : 0)} day{requestedDays !== 1 ? 's' : ''}
                  </dd>
                </div>
                {weekendSelections.some(s => s.included) && (
                  <div className="mt-2 text-xs text-amber-600 flex items-center gap-1">
                    <AlertTriangle className="h-3 w-3" />
                    Includes {weekendSelections.filter(s => s.included).length} weekend day{weekendSelections.filter(s => s.included).length > 1 ? 's' : ''}
                    ({weekendSelections.filter(s => s.included).reduce((sum, s) => sum + s.hours / 8, 0).toFixed(1)} days) - approver may adjust
                  </div>
                )}
                {formData.motivation && (
                  <div className="border-t border-gray-200 pt-4">
                    <dt className="text-gray-500">Motivation</dt>
                    <dd className="mt-1 text-gray-900">{formData.motivation}</dd>
                  </div>
                )}
                {uploadedDocuments.filter(d => d.status === 'success').length > 0 && (
                  <div className="border-t border-gray-200 pt-4">
                    <dt className="text-gray-500 mb-2">Supporting Documents</dt>
                    <dd className="space-y-2">
                      {uploadedDocuments
                        .filter(d => d.status === 'success')
                        .map(doc => (
                          <div key={doc.key} className="flex items-center gap-2">
                            <File className="h-4 w-4 text-green-600" />
                            <span className="text-sm text-gray-900">{doc.fileName}</span>
                          </div>
                        ))}
                    </dd>
                  </div>
                )}
              </dl>
            </div>
            <div className="flex justify-end gap-4">
              <button onClick={handleBack} className="btn-secondary" disabled={isSubmitting}>
                Back
              </button>
              <button
                onClick={handleSubmit}
                className="btn-primary flex items-center gap-2"
                disabled={isSubmitting || hasInsufficientBalance}
              >
                {isSubmitting && <Loader2 className="h-4 w-4 animate-spin" />}
                {isSubmitting ? 'Submitting...' : 'Submit Application'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
